import { useEffect } from 'react';

declare global {
  interface Window {
    gtag?: (command: string, action: string, parameters: any) => void;
  }
}

interface PerformanceResourceTiming extends PerformanceEntry {
  initiatorType: string;
  responseEnd: number;
  startTime: number;
}

const PerformanceMonitor = () => {
  useEffect(() => {
    // Only run in production and if Web Vitals is available
    if (process.env.NODE_ENV !== 'production') return;

    // Defer performance monitoring to idle time
    const initPerformanceMonitoring = () => {
      const reportWebVitals = (metric: any) => {
      // Log Core Web Vitals for debugging
      console.log(`${metric.name}: ${metric.value}`);
      
      // Send to analytics service if available
      if (window.gtag) {
        window.gtag('event', metric.name, {
          value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
          event_category: 'Web Vitals',
          event_label: metric.id,
          non_interaction: true,
        });
      }
    };

    // Load web-vitals library dynamically
    import('web-vitals').then(({ onCLS, onFID, onFCP, onLCP, onTTFB }) => {
      onCLS(reportWebVitals);
      onFID(reportWebVitals);
      onFCP(reportWebVitals);
      onLCP(reportWebVitals);
      onTTFB(reportWebVitals);
    }).catch(() => {
      // Silently fail if web-vitals is not available
    });

      // Monitor image loading performance
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const resourceEntry = entry as PerformanceResourceTiming;
          if (resourceEntry.initiatorType === 'img') {
            const duration = resourceEntry.responseEnd - resourceEntry.startTime;
            console.log(`Image loaded: ${entry.name} in ${duration}ms`);
            
            // Alert for slow image loads (>2s)
            if (duration > 2000 && process.env.NODE_ENV === 'development') {
              console.warn(`Slow image load detected: ${entry.name} took ${duration}ms`);
            }
          }
        }
      });

      observer.observe({ entryTypes: ['resource'] });

      return () => {
        observer.disconnect();
      };
    };

    // Use requestIdleCallback to defer monitoring
    if ('requestIdleCallback' in window) {
      requestIdleCallback(initPerformanceMonitoring, { timeout: 3000 });
    } else {
      setTimeout(initPerformanceMonitoring, 2000);
    }
  }, []);

  return null;
};

export default PerformanceMonitor;